import { useMemo, useState } from 'react';
import { Box, Button } from '@mui/material';
import _ from 'lodash';
import { useLocation } from 'react-router-dom';
import DataTable from 'components/common/data-table';
import TooltipIconButton from 'components/common/tooltip-icon-button';
import { Delete } from '@mui/icons-material';
import { useQuery } from 'react-query';
import { getDrawDownForecast, saveDrawDownData } from 'services/common-services';
import ActionBar from 'components/styled-components/ActionBar';
import { formatGridNumber } from 'utils/common-utils';
import { NumericEditor } from 'components/common/data-table/numericEditor';

const DrawdownForecast = () => {
  const { state } = useLocation();
  const [rowData, setRowData] = useState([]);
  const numericParams = (type: string) => {
    return {
      valueParser: 'Number(newValue)',
      valueGetter: (params: any) => {
        return Number(params.data[type]);
      },
      valueFormatter: formatGridNumber,
      cellEditor: NumericEditor,
      cellStyle: { textAlign: 'right' },
      filter: 'agNumberColumnFilter',
      editable: true,
    };
  };
  const drawdownColmDef = useMemo(() => {
    var colDef: any = [
      {
        field: 'id',
        headerName: 'SL.No',
      },
      {
        field: 'customer',
        headerName: 'Customer Name',
        editable: true,
        suppressSizeToFit: true,
      },
      {
        field: 'customerGroup',
        headerName: 'Customer Group',
        editable: true,
        suppressSizeToFit: true,
      },
      {
        field: 'currentType',
        headerName: 'Customer Type',
        editable: true,
        suppressSizeToFit: true,
      },
      {
        field: 'teamLeadId',
        headerName: 'RM TL',
        editable: true,
      },
      {
        field: 'indoRm',
        headerName: 'INDO RM',
        editable: true,
        suppressSizeToFit: true,
      },
      {
        field: 'currentLimit',
        headerName: 'Current Limit',
        valueFormatter: formatGridNumber,
        cellEditor: NumericEditor,
        cellStyle: { textAlign: 'right' },
        editable: true,
        suppressSizeToFit: true,
      },
      {
        field: 'currentOs',
        headerName: 'Current O/S',
        valueFormatter: formatGridNumber,
        cellEditor: NumericEditor,
        cellStyle: { textAlign: 'right' },
        editable: true,
        suppressSizeToFit: true,
      },
      {
        field: 'dealStatus',
        headerName: 'Deal Status',
        editable: true,
        suppressSizeToFit: true,
      },
      {
        field: 'janDrawDown',
        headerName: 'Planned Drawdown January',
        editable: true,
        suppressSizeToFit: true,
        children: [
          {
            field: 'janDrawDownW1',
            headerName: 'W1',
            ...numericParams('janDrawDownW1'),
          },
          {
            field: 'janDrawDownW2',
            headerName: 'W2',
            ...numericParams('janDrawDownW2'),
          },
          {
            field: 'janDrawDownW3',
            headerName: 'W3',
            ...numericParams('janDrawDownW3'),
          },
          {
            field: 'janDrawDownW4',
            headerName: 'W4',
            ...numericParams('janDrawDownW4'),
          },
          {
            field: 'janDrawDownTotal',
            headerName: 'Total Jan DrawDown',
            valueFormatter: formatGridNumber,
            cellStyle: { textAlign: 'right' },
            valueGetter:
              'Number(data.janDrawDownW1) + Number(data.janDrawDownW2) + Number(data.janDrawDownW3) + Number(data.janDrawDownW4)',
            suppressSizeToFit: true,
          },
        ],
      },
      {
        field: 'janRepayment',
        headerName: 'Planned Repayment January',
        editable: true,
        suppressSizeToFit: true,
        children: [
          {
            field: 'janRepaymentW1',
            headerName: 'W1',
            ...numericParams('janRepaymentW1'),
          },
          {
            field: 'janRepaymentW2',
            headerName: 'W2',
            ...numericParams('janRepaymentW2'),
          },
          {
            field: 'janRepaymentW3',
            headerName: 'W3',
            ...numericParams('janRepaymentW3'),
          },
          {
            field: 'janRepaymentW4',
            headerName: 'W4',
            ...numericParams('janRepaymentW4'),
          },
          {
            field: 'janRepaymentTotal',
            headerName: 'Total Jan Repayment',
            valueFormatter: formatGridNumber,
            cellStyle: { textAlign: 'right' },
            valueGetter:
              'Number(data.janRepaymentW1) + Number(data.janRepaymentW2) + Number(data.janRepaymentW3) + Number(data.janRepaymentW4)',
            suppressSizeToFit: true,
          },
        ],
      },
      {
        field: 'plannedDrawdownFeb',
        headerName: 'Planned Drawdown February',
        editable: true,
        suppressSizeToFit: true,
        children: [
          {
            field: 'febDrawDownW1',
            headerName: 'W1',
            ...numericParams('febDrawDownW1'),
          },
          {
            field: 'febDrawDownW2',
            headerName: 'W2',
            ...numericParams('febDrawDownW2'),
          },
          {
            field: 'febDrawDownW3',
            headerName: 'W3',
            ...numericParams('febDrawDownW3'),
          },
          {
            field: 'febDrawDownW4',
            headerName: 'W4',
            ...numericParams('febDrawDownW4'),
          },
          {
            field: 'febDrawDownTotal',
            headerName: 'Total Feb DrawDown',
            valueFormatter: formatGridNumber,
            cellStyle: { textAlign: 'right' },
            valueGetter:
              'Number(data.febDrawDownW1) + Number(data.febDrawDownW2) + Number(data.febDrawDownW3) + Number(data.febDrawDownW4)',
            suppressSizeToFit: true,
          },
        ],
      },
      {
        field: 'plannedRepaymentFeb',
        headerName: 'Planned Repayment February',
        editable: true,
        suppressSizeToFit: true,
        children: [
          {
            field: 'febRepaymentW1',
            headerName: 'W1',
            ...numericParams('febRepaymentW1'),
          },
          {
            field: 'febRepaymentW2',
            headerName: 'W2',
            ...numericParams('febRepaymentW2'),
          },
          {
            field: 'febRepaymentW3',
            headerName: 'W3',
            ...numericParams('febRepaymentW3'),
          },
          {
            field: 'febRepaymentW4',
            headerName: 'W4',
            ...numericParams('febRepaymentW4'),
          },
          {
            field: 'febRepaymentTotal',
            headerName: 'Total Feb Repayment',
            valueFormatter: formatGridNumber,
            cellStyle: { textAlign: 'right' },
            valueGetter:
              'Number(data.febRepaymentW1) + Number(data.febRepaymentW2) + Number(data.febRepaymentW3) + Number(data.febRepaymentW4)',
            suppressSizeToFit: true,
          },
        ],
      },
      {
        field: 'plannedDrawdownMar',
        headerName: 'Planned Drawdown March',
        editable: true,
        suppressSizeToFit: true,
        children: [
          {
            field: 'marDrawDownW1',
            headerName: 'W1',
            ...numericParams('marDrawDownW1'),
          },
          {
            field: 'marDrawDownW2',
            headerName: 'W2',
            ...numericParams('marDrawDownW2'),
          },
          {
            field: 'marDrawDownW3',
            headerName: 'W3',
            ...numericParams('marDrawDownW3'),
          },
          {
            field: 'marDrawDownW4',
            headerName: 'W4',
            ...numericParams('marDrawDownW4'),
          },
          {
            field: 'marDrawDownTotal',
            headerName: 'Total Mar DrawDown',
            valueFormatter: formatGridNumber,
            cellStyle: { textAlign: 'right' },
            valueGetter:
              'Number(data.marDrawDownW1) + Number(data.marDrawDownW2) + Number(data.marDrawDownW3) + Number(data.marDrawDownW4)',
            suppressSizeToFit: true,
          },
        ],
      },
      {
        field: 'plannedRepaymentMar',
        headerName: 'Planned Repayment March',
        editable: true,
        suppressSizeToFit: true,
        children: [
          {
            field: 'marRepaymentW1',
            headerName: 'W1',
            ...numericParams('marRepaymentW1'),
          },
          {
            field: 'marRepaymentW2',
            headerName: 'W2',
            ...numericParams('marRepaymentW2'),
          },
          {
            field: 'marRepaymentW3',
            headerName: 'W3',
            ...numericParams('marRepaymentW3'),
          },
          {
            field: 'marRepaymentW4',
            headerName: 'W4',
            ...numericParams('marRepaymentW4'),
          },
          {
            field: 'marRepaymentTotal',
            headerName: 'Total Mar Repayment',
            valueFormatter: formatGridNumber,
            cellStyle: { textAlign: 'right' },
            valueGetter:
              'Number(data.marRepaymentW1) + Number(data.marRepaymentW2) + Number(data.marRepaymentW3) + Number(data.marRepaymentW4)',
            suppressSizeToFit: true,
          },
        ],
      },
      {
        field: 'plannedDrawdownApr',
        headerName: 'Planned Drawdown April',
        editable: true,
        suppressSizeToFit: true,
        children: [
          {
            field: 'aprDrawDownW1',
            headerName: 'W1',
            ...numericParams('aprDrawDownW1'),
          },
          {
            field: 'aprDrawDownW2',
            headerName: 'W2',
            ...numericParams('aprDrawDownW2'),
          },
          {
            field: 'aprDrawDownW3',
            headerName: 'W3',
            ...numericParams('aprDrawDownW3'),
          },
          {
            field: 'aprDrawDownW4',
            headerName: 'W4',
            ...numericParams('aprDrawDownW4'),
          },
          {
            field: 'aprDrawDownTotal',
            headerName: 'Total Apr DrawDown',
            valueFormatter: formatGridNumber,
            cellStyle: { textAlign: 'right' },
            valueGetter:
              'Number(data.aprDrawDownW1) + Number(data.aprDrawDownW2) + Number(data.aprDrawDownW3) + Number(data.aprDrawDownW4)',
            suppressSizeToFit: true,
          },
        ],
      },
      {
        field: 'plannedRepaymentApr',
        headerName: 'Planned Repayment April',
        editable: true,
        suppressSizeToFit: true,
        children: [
          {
            field: 'aprRepaymentW1',
            headerName: 'W1',
            ...numericParams('aprRepaymentW1'),
          },
          {
            field: 'aprRepaymentW2',
            headerName: 'W2',
            ...numericParams('aprRepaymentW2'),
          },
          {
            field: 'aprRepaymentW3',
            headerName: 'W3',
            ...numericParams('aprRepaymentW3'),
          },
          {
            field: 'aprRepaymentW4',
            headerName: 'W4',
            ...numericParams('aprRepaymentW4'),
          },
          {
            field: 'aprRepaymentTotal',
            headerName: 'Total Apr Repayment',
            valueFormatter: formatGridNumber,
            cellStyle: { textAlign: 'right' },
            valueGetter:
              'Number(data.aprRepaymentW1) + Number(data.aprRepaymentW2) + Number(data.aprRepaymentW3) + Number(data.aprRepaymentW4)',
            suppressSizeToFit: true,
          },
        ],
      },
      {
        field: 'plannedDrawdownMay',
        headerName: 'Planned Drawdown May',
        editable: true,
        suppressSizeToFit: true,
        children: [
          {
            field: 'mayDrawDownW1',
            headerName: 'W1',
            ...numericParams('mayDrawDownW1'),
          },
          {
            field: 'mayDrawDownW2',
            headerName: 'W2',
            ...numericParams('mayDrawDownW2'),
          },
          {
            field: 'mayDrawDownW3',
            headerName: 'W3',
            ...numericParams('mayDrawDownW3'),
          },
          {
            field: 'mayDrawDownW4',
            headerName: 'W4',
            ...numericParams('mayDrawDownW4'),
          },
          {
            field: 'mayDrawDownTotal',
            headerName: 'Total May DrawDown',
            valueFormatter: formatGridNumber,
            cellStyle: { textAlign: 'right' },
            valueGetter:
              'Number(data.mayDrawDownW1) + Number(data.mayDrawDownW2) + Number(data.mayDrawDownW3) + Number(data.mayDrawDownW4)',
            suppressSizeToFit: true,
          },
        ],
      },
      {
        field: 'plannedRepaymentMay',
        headerName: 'Planned Repayment May',
        editable: true,
        suppressSizeToFit: true,
        children: [
          {
            field: 'mayRepaymentW1',
            headerName: 'W1',
            ...numericParams('mayRepaymentW1'),
          },
          {
            field: 'mayRepaymentW2',
            headerName: 'W2',
            ...numericParams('mayRepaymentW2'),
          },
          {
            field: 'mayRepaymentW3',
            headerName: 'W3',
            ...numericParams('mayRepaymentW3'),
          },
          {
            field: 'mayRepaymentW4',
            headerName: 'W4',
            ...numericParams('mayRepaymentW4'),
          },
          {
            field: 'mayRepaymentTotal',
            headerName: 'Total May Repayment',
            valueFormatter: formatGridNumber,
            cellStyle: { textAlign: 'right' },
            valueGetter:
              'Number(data.mayRepaymentW1) + Number(data.mayRepaymentW2) + Number(data.mayRepaymentW3) + Number(data.mayRepaymentW4)',
            suppressSizeToFit: true,
          },
        ],
      },
      {
        field: 'plannedDrawdownJune',
        headerName: 'Planned Drawdown June',
        editable: true,
        suppressSizeToFit: true,
        children: [
          {
            field: 'junDrawDownW1',
            headerName: 'W1',
            ...numericParams('junDrawDownW1'),
          },
          {
            field: 'junDrawDownW2',
            headerName: 'W2',
            ...numericParams('junDrawDownW2'),
          },
          {
            field: 'junDrawDownW3',
            headerName: 'W3',
            ...numericParams('junDrawDownW3'),
          },
          {
            field: 'junDrawDownW4',
            headerName: 'W4',
            ...numericParams('junDrawDownW4'),
          },
          {
            field: 'junDrawDownTotal',
            headerName: 'Total Jun DrawDown',
            valueFormatter: formatGridNumber,
            cellStyle: { textAlign: 'right' },
            valueGetter:
              'Number(data.junDrawDownW1) + Number(data.junDrawDownW2) + Number(data.junDrawDownW3) + Number(data.junDrawDownW4)',
            suppressSizeToFit: true,
          },
        ],
      },
      {
        field: 'plannedRepaymentJune',
        headerName: 'Planned Repayment June',
        editable: true,
        suppressSizeToFit: true,
        children: [
          {
            field: 'junRepaymentW1',
            headerName: 'W1',
            ...numericParams('junRepaymentW1'),
          },
          {
            field: 'junRepaymentW2',
            headerName: 'W2',
            ...numericParams('junRepaymentW2'),
          },
          {
            field: 'junRepaymentW3',
            headerName: 'W3',
            ...numericParams('junRepaymentW3'),
          },
          {
            field: 'junRepaymentW4',
            headerName: 'W4',
            ...numericParams('junRepaymentW4'),
          },
          {
            field: 'junRepaymentTotal',
            headerName: 'Total Jun Repayment',
            valueFormatter: formatGridNumber,
            cellStyle: { textAlign: 'right' },
            valueGetter:
              'Number(data.junRepaymentW1) + Number(data.junRepaymentW2) + Number(data.junRepaymentW3) + Number(data.junRepaymentW4)',
            suppressSizeToFit: true,
          },
        ],
      },
      {
        field: 'plannedDrawdownJuly',
        headerName: 'Planned Drawdown July',
        editable: true,
        suppressSizeToFit: true,
        children: [
          {
            field: 'julDrawDownW1',
            headerName: 'W1',
            ...numericParams('julDrawDownW1'),
          },
          {
            field: 'julDrawDownW2',
            headerName: 'W2',
            ...numericParams('julDrawDownW2'),
          },
          {
            field: 'julDrawDownW3',
            headerName: 'W3',
            ...numericParams('julDrawDownW3'),
          },
          {
            field: 'julDrawDownW4',
            headerName: 'W4',
            ...numericParams('julDrawDownW4'),
          },
          {
            field: 'julDrawDownTotal',
            headerName: 'Total Jul DrawDown',
            valueFormatter: formatGridNumber,
            cellStyle: { textAlign: 'right' },
            valueGetter:
              'Number(data.julDrawDownW1) + Number(data.julDrawDownW2) + Number(data.julDrawDownW3) + Number(data.julDrawDownW4)',
            suppressSizeToFit: true,
          },
        ],
      },
      {
        field: 'plannedRepaymentJuly',
        headerName: 'Planned Repayment July',
        editable: true,
        suppressSizeToFit: true,
        children: [
          {
            field: 'julRepaymentW1',
            headerName: 'W1',
            ...numericParams('julRepaymentW1'),
          },
          {
            field: 'julRepaymentW2',
            headerName: 'W2',
            ...numericParams('julRepaymentW2'),
          },
          {
            field: 'julRepaymentW3',
            headerName: 'W3',
            ...numericParams('julRepaymentW3'),
          },
          {
            field: 'julRepaymentW4',
            headerName: 'W4',
            ...numericParams('julRepaymentW4'),
          },
          {
            field: 'julRepaymentTotal',
            headerName: 'Total Jul Repayment',
            valueFormatter: formatGridNumber,
            cellStyle: { textAlign: 'right' },
            valueGetter:
              'Number(data.julRepaymentW1) + Number(data.julRepaymentW2) + Number(data.julRepaymentW3) + Number(data.julRepaymentW4)',
            suppressSizeToFit: true,
          },
        ],
      },
      {
        field: 'plannedDrawdownAug',
        headerName: 'Planned Drawdown August',
        editable: true,
        suppressSizeToFit: true,
        children: [
          {
            field: 'augDrawDownW1',
            headerName: 'W1',
            ...numericParams('augDrawDownW1'),
          },
          {
            field: 'augDrawDownW2',
            headerName: 'W2',
            ...numericParams('augDrawDownW2'),
          },
          {
            field: 'augDrawDownW3',
            headerName: 'W3',
            ...numericParams('augDrawDownW3'),
          },
          {
            field: 'augDrawDownW4',
            headerName: 'W4',
            ...numericParams('augDrawDownW4'),
          },
          {
            field: 'augDrawDownTotal',
            headerName: 'Total Aug DrawDown',
            valueFormatter: formatGridNumber,
            cellStyle: { textAlign: 'right' },
            valueGetter:
              'Number(data.augDrawDownW1) + Number(data.augDrawDownW2) + Number(data.augDrawDownW3) + Number(data.augDrawDownW4)',
            suppressSizeToFit: true,
          },
        ],
      },
      {
        field: 'plannedRepaymentAug',
        headerName: 'Planned Repayment August',
        editable: true,
        suppressSizeToFit: true,
        children: [
          {
            field: 'augRepaymentW1',
            headerName: 'W1',
            ...numericParams('augRepaymentW1'),
          },
          {
            field: 'augRepaymentW2',
            headerName: 'W2',
            ...numericParams('augRepaymentW2'),
          },
          {
            field: 'augRepaymentW3',
            headerName: 'W3',
            ...numericParams('augRepaymentW3'),
          },
          {
            field: 'augRepaymentW4',
            headerName: 'W4',
            ...numericParams('augRepaymentW4'),
          },
          {
            field: 'augRepaymentTotal',
            headerName: 'Total Aug Repayment',
            valueFormatter: formatGridNumber,
            cellStyle: { textAlign: 'right' },
            valueGetter:
              'Number(data.augRepaymentW1) + Number(data.augRepaymentW2) + Number(data.augRepaymentW3) + Number(data.augRepaymentW4)',
            suppressSizeToFit: true,
          },
        ],
      },
      {
        field: 'plannedDrawdownSep',
        headerName: 'Planned Drawdown September',
        editable: true,
        suppressSizeToFit: true,
        children: [
          {
            field: 'sepDrawDownW1',
            headerName: 'W1',
            ...numericParams('sepDrawDownW1'),
          },
          {
            field: 'sepDrawDownW2',
            headerName: 'W2',
            ...numericParams('sepDrawDownW2'),
          },
          {
            field: 'sepDrawDownW3',
            headerName: 'W3',
            ...numericParams('sepDrawDownW3'),
          },
          {
            field: 'sepDrawDownW4',
            headerName: 'W4',
            ...numericParams('sepDrawDownW4'),
          },
          {
            field: 'sepDrawDownTotal',
            headerName: 'Total Sep DrawDown',
            valueFormatter: formatGridNumber,
            cellStyle: { textAlign: 'right' },
            valueGetter:
              'Number(data.sepDrawDownW1) + Number(data.sepDrawDownW2) + Number(data.sepDrawDownW3) + Number(data.sepDrawDownW4)',
            suppressSizeToFit: true,
          },
        ],
      },
      {
        field: 'plannedRepaymentSep',
        headerName: 'Planned Repayment September',
        editable: true,
        suppressSizeToFit: true,
        children: [
          {
            field: 'sepRepaymentW1',
            headerName: 'W1',
            ...numericParams('sepRepaymentW1'),
          },
          {
            field: 'sepRepaymentW2',
            headerName: 'W2',
            ...numericParams('sepRepaymentW2'),
          },
          {
            field: 'sepRepaymentW3',
            headerName: 'W3',
            ...numericParams('sepRepaymentW3'),
          },
          {
            field: 'sepRepaymentW4',
            headerName: 'W4',
            ...numericParams('sepRepaymentW4'),
          },
          {
            field: 'sepRepaymentTotal',
            headerName: 'Total Sep Repayment',
            valueFormatter: formatGridNumber,
            cellStyle: { textAlign: 'right' },
            valueGetter:
              'Number(data.sepRepaymentW1) + Number(data.sepRepaymentW2) + Number(data.sepRepaymentW3) + Number(data.sepRepaymentW4)',
            suppressSizeToFit: true,
          },
        ],
      },
      {
        field: 'plannedDrawdownOct',
        headerName: 'Planned Drawdown October',
        editable: true,
        suppressSizeToFit: true,
        children: [
          {
            field: 'octDrawDownW1',
            headerName: 'W1',
            ...numericParams('octDrawDownW1'),
          },
          {
            field: 'octDrawDownW2',
            headerName: 'W2',
            ...numericParams('octDrawDownW2'),
          },
          {
            field: 'octDrawDownW3',
            headerName: 'W3',
            ...numericParams('octDrawDownW3'),
          },
          {
            field: 'octDrawDownW4',
            headerName: 'W4',
            ...numericParams('octDrawDownW4'),
          },
          {
            field: 'octDrawDownTotal',
            headerName: 'Total Oct DrawDown',
            valueFormatter: formatGridNumber,
            cellStyle: { textAlign: 'right' },
            valueGetter:
              'Number(data.octDrawDownW1) + Number(data.octDrawDownW2) + Number(data.octDrawDownW3) + Number(data.octDrawDownW4)',
            suppressSizeToFit: true,
          },
        ],
      },
      {
        field: 'plannedRepaymentOct',
        headerName: 'Planned Repayment October',
        editable: true,
        suppressSizeToFit: true,
        children: [
          {
            field: 'octRepaymentW1',
            headerName: 'W1',
            ...numericParams('octRepaymentW1'),
          },
          {
            field: 'octRepaymentW2',
            headerName: 'W2',
            ...numericParams('octRepaymentW2'),
          },
          {
            field: 'octRepaymentW3',
            headerName: 'W3',
            ...numericParams('octRepaymentW3'),
          },
          {
            field: 'octRepaymentW4',
            headerName: 'W4',
            ...numericParams('octRepaymentW4'),
          },
          {
            field: 'octRepaymentTotal',
            headerName: 'Total Oct Repayment',
            valueFormatter: formatGridNumber,
            cellStyle: { textAlign: 'right' },
            valueGetter:
              'Number(data.octRepaymentW1) + Number(data.octRepaymentW2) + Number(data.octRepaymentW3) + Number(data.octRepaymentW4)',
            suppressSizeToFit: true,
          },
        ],
      },
      {
        field: 'plannedDrawdownNov',
        headerName: 'Planned Drawdown November',
        editable: true,
        suppressSizeToFit: true,
        children: [
          {
            field: 'novDrawDownW1',
            headerName: 'W1',
            ...numericParams('novDrawDownW1'),
          },
          {
            field: 'novDrawDownW2',
            headerName: 'W2',
            ...numericParams('novDrawDownW2'),
          },
          {
            field: 'novDrawDownW3',
            headerName: 'W3',
            ...numericParams('novDrawDownW3'),
          },
          {
            field: 'novDrawDownW4',
            headerName: 'W4',
            ...numericParams('novDrawDownW4'),
          },
          {
            field: 'novDrawDownTotal',
            headerName: 'Total Nov DrawDown',
            valueFormatter: formatGridNumber,
            cellStyle: { textAlign: 'right' },
            valueGetter:
              'Number(data.novDrawDownW1) + Number(data.novDrawDownW2) + Number(data.novDrawDownW3) + Number(data.novDrawDownW4)',
            suppressSizeToFit: true,
          },
        ],
      },
      {
        field: 'plannedRepaymentNov',
        headerName: 'Planned Repayment November',
        editable: true,
        suppressSizeToFit: true,
        children: [
          {
            field: 'novRepaymentW1',
            headerName: 'W1',
            ...numericParams('novRepaymentW1'),
          },
          {
            field: 'novRepaymentW2',
            headerName: 'W2',
            ...numericParams('novRepaymentW2'),
          },
          {
            field: 'novRepaymentW3',
            headerName: 'W3',
            ...numericParams('novRepaymentW3'),
          },
          {
            field: 'novRepaymentW4',
            headerName: 'W4',
            ...numericParams('novRepaymentW4'),
          },
          {
            field: 'novRepaymentTotal',
            headerName: 'Total Nov Repayment',
            valueFormatter: formatGridNumber,
            cellStyle: { textAlign: 'right' },
            valueGetter:
              'Number(data.novRepaymentW1) + Number(data.novRepaymentW2) + Number(data.novRepaymentW3) + Number(data.novRepaymentW4)',
            suppressSizeToFit: true,
          },
        ],
      },
      {
        field: 'plannedDrawdownDec',
        headerName: 'Planned Drawdown December',
        editable: true,
        suppressSizeToFit: true,
        children: [
          {
            field: 'decDrawDownW1',
            headerName: 'W1',
            ...numericParams('decDrawDownW1'),
          },
          {
            field: 'decDrawDownW2',
            headerName: 'W2',
            ...numericParams('decDrawDownW2'),
          },
          {
            field: 'decDrawDownW3',
            headerName: 'W3',
            ...numericParams('decDrawDownW3'),
          },
          {
            field: 'decDrawDownW4',
            headerName: 'W4',
            ...numericParams('decDrawDownW4'),
          },
          {
            field: 'decDrawDownTotal',
            headerName: 'Total Dec DrawDown',
            valueFormatter: formatGridNumber,
            cellStyle: { textAlign: 'right' },
            valueGetter:
              'Number(data.decDrawDownW1) + Number(data.decDrawDownW2) + Number(data.decDrawDownW3) + Number(data.decDrawDownW4)',
            suppressSizeToFit: true,
          },
        ],
      },
      {
        field: 'plannedRepaymentDec',
        headerName: 'Planned Repayment December',
        editable: true,
        suppressSizeToFit: true,
        children: [
          {
            field: 'decRepaymentW1',
            headerName: 'W1',
            ...numericParams('decRepaymentW1'),
          },
          {
            field: 'decRepaymentW2',
            headerName: 'W2',
            ...numericParams('decRepaymentW2'),
          },
          {
            field: 'decRepaymentW3',
            headerName: 'W3',
            ...numericParams('decRepaymentW3'),
          },
          {
            field: 'decRepaymentW4',
            headerName: 'W4',
            ...numericParams('decRepaymentW4'),
          },
          {
            field: 'decRepaymentTotal',
            headerName: 'Total Dec Repayment',
            valueGetter:
              'Number(data.decRepaymentW1) + Number(data.decRepaymentW2) + Number(data.decRepaymentW3) + Number(data.decRepaymentW4)',
            valueFormatter: formatGridNumber,
            cellStyle: { textAlign: 'right' },
            suppressSizeToFit: true,
          },
        ],
      },
      {
        field: 'netDrawDownRepayment',
        headerName: 'Net DrawDown/Repayment',
        editable: true,
        suppressSizeToFit: true,
        children: [
          {
            field: 'janNetDdRepay',
            headerName: 'JAN',
            valueFormatter: formatGridNumber,
            cellStyle: { textAlign: 'right' },
            valueGetter:
              'Number(data.janDrawDownW1) + Number(data.janDrawDownW2) + Number(data.janDrawDownW3) + Number(data.janDrawDownW4) - Number(data.janRepaymentW1) + Number(data.janRepaymentW2) + Number(data.janRepaymentW3) + Number(data.janRepaymentW4)',
          },
          {
            field: 'febNetDdRepay',
            headerName: 'FEB',
            valueFormatter: formatGridNumber,
            cellStyle: { textAlign: 'right' },
            valueGetter:
              'Number(data.febDrawDownW1) + Number(data.febDrawDownW2) + Number(data.febDrawDownW3) + Number(data.febDrawDownW4) - Number(data.febRepaymentW1) + Number(data.febRepaymentW2) + Number(data.febRepaymentW3) + Number(data.febRepaymentW4)',
          },
          {
            field: 'marNetDdRepay',
            headerName: 'MAR',
            valueFormatter: formatGridNumber,
            cellStyle: { textAlign: 'right' },
            valueGetter:
              'Number(data.marDrawDownW1) + Number(data.marDrawDownW2) + Number(data.marDrawDownW3) + Number(data.marDrawDownW4) - Number(data.marRepaymentW1) + Number(data.marRepaymentW2) + Number(data.marRepaymentW3) + Number(data.marRepaymentW4)',
          },
          {
            field: 'aprNetDdRepay',
            headerName: 'APR',
            valueFormatter: formatGridNumber,
            cellStyle: { textAlign: 'right' },
            valueGetter:
              'Number(data.aprDrawDownW1) + Number(data.aprDrawDownW2) + Number(data.aprDrawDownW3) + Number(data.aprDrawDownW4) - Number(data.aprRepaymentW1) + Number(data.aprRepaymentW2) + Number(data.aprRepaymentW3) + Number(data.aprRepaymentW4)',
          },
          {
            field: 'mayNetDdRepay',
            headerName: 'MAY',
            valueFormatter: formatGridNumber,
            cellStyle: { textAlign: 'right' },
            valueGetter:
              'Number(data.mayDrawDownW1) + Number(data.mayDrawDownW2) + Number(data.mayDrawDownW3) + Number(data.mayDrawDownW4) - Number(data.mayRepaymentW1) + Number(data.mayRepaymentW2) + Number(data.mayRepaymentW3) + Number(data.mayRepaymentW4)',
          },
          {
            field: 'junNetDdRepay',
            headerName: 'JUN',
            valueFormatter: formatGridNumber,
            cellStyle: { textAlign: 'right' },
            valueGetter:
              'Number(data.junDrawDownW1) + Number(data.junDrawDownW2) + Number(data.junDrawDownW3) + Number(data.junDrawDownW4) - Number(data.junRepaymentW1) + Number(data.junRepaymentW2) + Number(data.junRepaymentW3) + Number(data.junRepaymentW4)',
          },
          {
            field: 'julNetDdRepay',
            headerName: 'JUL',
            valueFormatter: formatGridNumber,
            cellStyle: { textAlign: 'right' },
            valueGetter:
              'Number(data.julDrawDownW1) + Number(data.julDrawDownW2) + Number(data.julDrawDownW3) + Number(data.julDrawDownW4) - Number(data.julRepaymentW1) + Number(data.julRepaymentW2) + Number(data.julRepaymentW3) + Number(data.julRepaymentW4)',
          },
          {
            field: 'augNetDdRepay',
            headerName: 'AUG',
            valueFormatter: formatGridNumber,
            cellStyle: { textAlign: 'right' },
            valueGetter:
              'Number(data.augDrawDownW1) + Number(data.augDrawDownW2) + Number(data.augDrawDownW3) + Number(data.augDrawDownW4) - Number(data.augRepaymentW1) + Number(data.augRepaymentW2) + Number(data.augRepaymentW3) + Number(data.augRepaymentW4)',
          },
          {
            field: 'sepNetDdRepay',
            headerName: 'SEP',
            valueFormatter: formatGridNumber,
            cellStyle: { textAlign: 'right' },
            valueGetter:
              'Number(data.sepDrawDownW1) + Number(data.sepDrawDownW2) + Number(data.sepDrawDownW3) + Number(data.sepDrawDownW4) - Number(data.sepRepaymentW1) + Number(data.sepRepaymentW2) + Number(data.sepRepaymentW3) + Number(data.sepRepaymentW4)',
          },
          {
            field: 'octNetDdRepay',
            headerName: 'OCT',
            valueFormatter: formatGridNumber,
            cellStyle: { textAlign: 'right' },
            valueGetter:
              'Number(data.octDrawDownW1) + Number(data.octDrawDownW2) + Number(data.octDrawDownW3) + Number(data.octDrawDownW4) - Number(data.octRepaymentW1) + Number(data.octRepaymentW2) + Number(data.octRepaymentW3) + Number(data.octRepaymentW4)',
          },
          {
            field: 'novNetDdRepay',
            headerName: 'NOV',
            valueFormatter: formatGridNumber,
            cellStyle: { textAlign: 'right' },
            valueGetter:
              'Number(data.novDrawDownW1) + Number(data.novDrawDownW2) + Number(data.novDrawDownW3) + Number(data.novDrawDownW4) - Number(data.novRepaymentW1) + Number(data.novRepaymentW2) + Number(data.novRepaymentW3) + Number(data.novRepaymentW4)',
          },
          {
            field: 'decNetDdRepay',
            headerName: 'DEC',
            valueFormatter: formatGridNumber,
            cellStyle: { textAlign: 'right' },
            valueGetter:
              'Number(data.decDrawDownW1) + Number(data.decDrawDownW2) + Number(data.decDrawDownW3) + Number(data.decDrawDownW4) - Number(data.decRepaymentW1) + Number(data.decRepaymentW2) + Number(data.decRepaymentW3) + Number(data.decRepaymentW4)',
          },
        ],
      },
      {
        field: 'endingOutstanding',
        headerName: 'Ending Outstanding',
        editable: true,
        suppressSizeToFit: true,
        children: [
          {
            field: 'janEndingOutstanding',
            headerName: 'JAN',
            valueFormatter: formatGridNumber,
            cellStyle: { textAlign: 'right' },
            valueGetter:
              'Number(data.currentOs) + (Number(data.janDrawDownW1) + Number(data.janDrawDownW2) + Number(data.janDrawDownW3) + Number(data.janDrawDownW4) - Number(data.janRepaymentW1) + Number(data.janRepaymentW2) + Number(data.janRepaymentW3) + Number(data.janRepaymentW4))',
          },
          {
            field: 'febEndingOutstanding',
            headerName: 'FEB',
            valueFormatter: formatGridNumber,
            cellStyle: { textAlign: 'right' },
            valueGetter:
              'Number(data.currentOs) + (Number(data.janDrawDownW1) + Number(data.janDrawDownW2) + Number(data.janDrawDownW3) + Number(data.janDrawDownW4) - Number(data.janRepaymentW1) + Number(data.janRepaymentW2) + Number(data.janRepaymentW3) + Number(data.janRepaymentW4)) + (Number(data.febDrawDownW1) + Number(data.febDrawDownW2) + Number(data.febDrawDownW3) + Number(data.febDrawDownW4) - Number(data.febRepaymentW1) + Number(data.febRepaymentW2) + Number(data.febRepaymentW3) + Number(data.febRepaymentW4))',
          },
          {
            field: 'marEndingOutstanding',
            headerName: 'MAR',
            valueFormatter: formatGridNumber,
            cellStyle: { textAlign: 'right' },
            valueGetter:
              'Number(data.currentOs) + (Number(data.janDrawDownW1) + Number(data.janDrawDownW2) + Number(data.janDrawDownW3) + Number(data.janDrawDownW4) - Number(data.janRepaymentW1) + Number(data.janRepaymentW2) + Number(data.janRepaymentW3) + Number(data.janRepaymentW4)) + (Number(data.febDrawDownW1) + Number(data.febDrawDownW2) + Number(data.febDrawDownW3) + Number(data.febDrawDownW4) - Number(data.febRepaymentW1) + Number(data.febRepaymentW2) + Number(data.febRepaymentW3) + Number(data.febRepaymentW4)) + (Number(data.marDrawDownW1) + Number(data.marDrawDownW2) + Number(data.marDrawDownW3) + Number(data.marDrawDownW4) - Number(data.marRepaymentW1) + Number(data.marRepaymentW2) + Number(data.marRepaymentW3) + Number(data.marRepaymentW4))',
          },
          {
            field: 'aprEndingOutstanding',
            headerName: 'APR',
            valueFormatter: formatGridNumber,
            cellStyle: { textAlign: 'right' },
            valueGetter:
              'Number(data.currentOs) + (Number(data.janDrawDownW1) + Number(data.janDrawDownW2) + Number(data.janDrawDownW3) + Number(data.janDrawDownW4) - Number(data.janRepaymentW1) + Number(data.janRepaymentW2) + Number(data.janRepaymentW3) + Number(data.janRepaymentW4)) + (Number(data.febDrawDownW1) + Number(data.febDrawDownW2) + Number(data.febDrawDownW3) + Number(data.febDrawDownW4) - Number(data.febRepaymentW1) + Number(data.febRepaymentW2) + Number(data.febRepaymentW3) + Number(data.febRepaymentW4)) + (Number(data.marDrawDownW1) + Number(data.marDrawDownW2) + Number(data.marDrawDownW3) + Number(data.marDrawDownW4) - Number(data.marRepaymentW1) + Number(data.marRepaymentW2) + Number(data.marRepaymentW3) + Number(data.marRepaymentW4)) + (Number(data.aprDrawDownW1) + Number(data.aprDrawDownW2) + Number(data.aprDrawDownW3) + Number(data.aprDrawDownW4) - Number(data.aprRepaymentW1) + Number(data.aprRepaymentW2) + Number(data.aprRepaymentW3) + Number(data.aprRepaymentW4))',
          },
          {
            field: 'mayEndingOutstanding',
            headerName: 'MAY',
            valueFormatter: formatGridNumber,
            cellStyle: { textAlign: 'right' },
            valueGetter:
              'Number(data.currentOs) + (Number(data.janDrawDownW1) + Number(data.janDrawDownW2) + Number(data.janDrawDownW3) + Number(data.janDrawDownW4) - Number(data.janRepaymentW1) + Number(data.janRepaymentW2) + Number(data.janRepaymentW3) + Number(data.janRepaymentW4)) + (Number(data.febDrawDownW1) + Number(data.febDrawDownW2) + Number(data.febDrawDownW3) + Number(data.febDrawDownW4) - Number(data.febRepaymentW1) + Number(data.febRepaymentW2) + Number(data.febRepaymentW3) + Number(data.febRepaymentW4)) + (Number(data.marDrawDownW1) + Number(data.marDrawDownW2) + Number(data.marDrawDownW3) + Number(data.marDrawDownW4) - Number(data.marRepaymentW1) + Number(data.marRepaymentW2) + Number(data.marRepaymentW3) + Number(data.marRepaymentW4)) + (Number(data.aprDrawDownW1) + Number(data.aprDrawDownW2) + Number(data.aprDrawDownW3) + Number(data.aprDrawDownW4) - Number(data.aprRepaymentW1) + Number(data.aprRepaymentW2) + Number(data.aprRepaymentW3) + Number(data.aprRepaymentW4)) + (Number(data.mayDrawDownW1) + Number(data.mayDrawDownW2) + Number(data.mayDrawDownW3) + Number(data.mayDrawDownW4) - Number(data.mayRepaymentW1) + Number(data.mayRepaymentW2) + Number(data.mayRepaymentW3) + Number(data.mayRepaymentW4))',
          },
          {
            field: 'junEndingOutstanding',
            headerName: 'JUN',
            valueFormatter: formatGridNumber,
            cellStyle: { textAlign: 'right' },
            valueGetter:
              'Number(data.currentOs) + (Number(data.janDrawDownW1) + Number(data.janDrawDownW2) + Number(data.janDrawDownW3) + Number(data.janDrawDownW4) - Number(data.janRepaymentW1) + Number(data.janRepaymentW2) + Number(data.janRepaymentW3) + Number(data.janRepaymentW4)) + (Number(data.febDrawDownW1) + Number(data.febDrawDownW2) + Number(data.febDrawDownW3) + Number(data.febDrawDownW4) - Number(data.febRepaymentW1) + Number(data.febRepaymentW2) + Number(data.febRepaymentW3) + Number(data.febRepaymentW4)) + (Number(data.marDrawDownW1) + Number(data.marDrawDownW2) + Number(data.marDrawDownW3) + Number(data.marDrawDownW4) - Number(data.marRepaymentW1) + Number(data.marRepaymentW2) + Number(data.marRepaymentW3) + Number(data.marRepaymentW4)) + (Number(data.aprDrawDownW1) + Number(data.aprDrawDownW2) + Number(data.aprDrawDownW3) + Number(data.aprDrawDownW4) - Number(data.aprRepaymentW1) + Number(data.aprRepaymentW2) + Number(data.aprRepaymentW3) + Number(data.aprRepaymentW4)) + (Number(data.mayDrawDownW1) + Number(data.mayDrawDownW2) + Number(data.mayDrawDownW3) + Number(data.mayDrawDownW4) - Number(data.mayRepaymentW1) + Number(data.mayRepaymentW2) + Number(data.mayRepaymentW3) + Number(data.mayRepaymentW4)) + (Number(data.junDrawDownW1) + Number(data.junDrawDownW2) + Number(data.junDrawDownW3) + Number(data.junDrawDownW4) - Number(data.junRepaymentW1) + Number(data.junRepaymentW2) + Number(data.junRepaymentW3) + Number(data.junRepaymentW4))',
          },
          {
            field: 'julEndingOutstanding',
            headerName: 'JUL',
            valueFormatter: formatGridNumber,
            cellStyle: { textAlign: 'right' },
            valueGetter:
              'Number(data.currentOs) + (Number(data.janDrawDownW1) + Number(data.janDrawDownW2) + Number(data.janDrawDownW3) + Number(data.janDrawDownW4) - Number(data.janRepaymentW1) + Number(data.janRepaymentW2) + Number(data.janRepaymentW3) + Number(data.janRepaymentW4)) + (Number(data.febDrawDownW1) + Number(data.febDrawDownW2) + Number(data.febDrawDownW3) + Number(data.febDrawDownW4) - Number(data.febRepaymentW1) + Number(data.febRepaymentW2) + Number(data.febRepaymentW3) + Number(data.febRepaymentW4)) + (Number(data.marDrawDownW1) + Number(data.marDrawDownW2) + Number(data.marDrawDownW3) + Number(data.marDrawDownW4) - Number(data.marRepaymentW1) + Number(data.marRepaymentW2) + Number(data.marRepaymentW3) + Number(data.marRepaymentW4)) + (Number(data.aprDrawDownW1) + Number(data.aprDrawDownW2) + Number(data.aprDrawDownW3) + Number(data.aprDrawDownW4) - Number(data.aprRepaymentW1) + Number(data.aprRepaymentW2) + Number(data.aprRepaymentW3) + Number(data.aprRepaymentW4)) + (Number(data.mayDrawDownW1) + Number(data.mayDrawDownW2) + Number(data.mayDrawDownW3) + Number(data.mayDrawDownW4) - Number(data.mayRepaymentW1) + Number(data.mayRepaymentW2) + Number(data.mayRepaymentW3) + Number(data.mayRepaymentW4)) + (Number(data.junDrawDownW1) + Number(data.junDrawDownW2) + Number(data.junDrawDownW3) + Number(data.junDrawDownW4) - Number(data.junRepaymentW1) + Number(data.junRepaymentW2) + Number(data.junRepaymentW3) + Number(data.junRepaymentW4)) + (Number(data.julDrawDownW1) + Number(data.julDrawDownW2) + Number(data.julDrawDownW3) + Number(data.julDrawDownW4) - Number(data.julRepaymentW1) + Number(data.julRepaymentW2) + Number(data.julRepaymentW3) + Number(data.julRepaymentW4))',
          },
          {
            field: 'augEndingOutstanding',
            headerName: 'AUG',
            valueFormatter: formatGridNumber,
            cellStyle: { textAlign: 'right' },
            valueGetter:
              'Number(data.currentOs) + (Number(data.janDrawDownW1) + Number(data.janDrawDownW2) + Number(data.janDrawDownW3) + Number(data.janDrawDownW4) - Number(data.janRepaymentW1) + Number(data.janRepaymentW2) + Number(data.janRepaymentW3) + Number(data.janRepaymentW4)) + (Number(data.febDrawDownW1) + Number(data.febDrawDownW2) + Number(data.febDrawDownW3) + Number(data.febDrawDownW4) - Number(data.febRepaymentW1) + Number(data.febRepaymentW2) + Number(data.febRepaymentW3) + Number(data.febRepaymentW4)) + (Number(data.marDrawDownW1) + Number(data.marDrawDownW2) + Number(data.marDrawDownW3) + Number(data.marDrawDownW4) - Number(data.marRepaymentW1) + Number(data.marRepaymentW2) + Number(data.marRepaymentW3) + Number(data.marRepaymentW4)) + (Number(data.aprDrawDownW1) + Number(data.aprDrawDownW2) + Number(data.aprDrawDownW3) + Number(data.aprDrawDownW4) - Number(data.aprRepaymentW1) + Number(data.aprRepaymentW2) + Number(data.aprRepaymentW3) + Number(data.aprRepaymentW4)) + (Number(data.mayDrawDownW1) + Number(data.mayDrawDownW2) + Number(data.mayDrawDownW3) + Number(data.mayDrawDownW4) - Number(data.mayRepaymentW1) + Number(data.mayRepaymentW2) + Number(data.mayRepaymentW3) + Number(data.mayRepaymentW4)) + (Number(data.junDrawDownW1) + Number(data.junDrawDownW2) + Number(data.junDrawDownW3) + Number(data.junDrawDownW4) - Number(data.junRepaymentW1) + Number(data.junRepaymentW2) + Number(data.junRepaymentW3) + Number(data.junRepaymentW4)) + (Number(data.julDrawDownW1) + Number(data.julDrawDownW2) + Number(data.julDrawDownW3) + Number(data.julDrawDownW4) - Number(data.julRepaymentW1) + Number(data.julRepaymentW2) + Number(data.julRepaymentW3) + Number(data.julRepaymentW4)) + (Number(data.augDrawDownW1) + Number(data.augDrawDownW2) + Number(data.augDrawDownW3) + Number(data.augDrawDownW4) - Number(data.augRepaymentW1) + Number(data.augRepaymentW2) + Number(data.augRepaymentW3) + Number(data.augRepaymentW4))',
          },
          {
            field: 'sepEndingOutstanding',
            headerName: 'SEP',
            valueFormatter: formatGridNumber,
            cellStyle: { textAlign: 'right' },
            valueGetter:
              'Number(data.currentOs) + (Number(data.janDrawDownW1) + Number(data.janDrawDownW2) + Number(data.janDrawDownW3) + Number(data.janDrawDownW4) - Number(data.janRepaymentW1) + Number(data.janRepaymentW2) + Number(data.janRepaymentW3) + Number(data.janRepaymentW4)) + (Number(data.febDrawDownW1) + Number(data.febDrawDownW2) + Number(data.febDrawDownW3) + Number(data.febDrawDownW4) - Number(data.febRepaymentW1) + Number(data.febRepaymentW2) + Number(data.febRepaymentW3) + Number(data.febRepaymentW4)) + (Number(data.marDrawDownW1) + Number(data.marDrawDownW2) + Number(data.marDrawDownW3) + Number(data.marDrawDownW4) - Number(data.marRepaymentW1) + Number(data.marRepaymentW2) + Number(data.marRepaymentW3) + Number(data.marRepaymentW4)) + (Number(data.aprDrawDownW1) + Number(data.aprDrawDownW2) + Number(data.aprDrawDownW3) + Number(data.aprDrawDownW4) - Number(data.aprRepaymentW1) + Number(data.aprRepaymentW2) + Number(data.aprRepaymentW3) + Number(data.aprRepaymentW4)) + (Number(data.mayDrawDownW1) + Number(data.mayDrawDownW2) + Number(data.mayDrawDownW3) + Number(data.mayDrawDownW4) - Number(data.mayRepaymentW1) + Number(data.mayRepaymentW2) + Number(data.mayRepaymentW3) + Number(data.mayRepaymentW4)) + (Number(data.junDrawDownW1) + Number(data.junDrawDownW2) + Number(data.junDrawDownW3) + Number(data.junDrawDownW4) - Number(data.junRepaymentW1) + Number(data.junRepaymentW2) + Number(data.junRepaymentW3) + Number(data.junRepaymentW4)) + (Number(data.julDrawDownW1) + Number(data.julDrawDownW2) + Number(data.julDrawDownW3) + Number(data.julDrawDownW4) - Number(data.julRepaymentW1) + Number(data.julRepaymentW2) + Number(data.julRepaymentW3) + Number(data.julRepaymentW4)) + (Number(data.augDrawDownW1) + Number(data.augDrawDownW2) + Number(data.augDrawDownW3) + Number(data.augDrawDownW4) - Number(data.augRepaymentW1) + Number(data.augRepaymentW2) + Number(data.augRepaymentW3) + Number(data.augRepaymentW4)) + (Number(data.sepDrawDownW1) + Number(data.sepDrawDownW2) + Number(data.sepDrawDownW3) + Number(data.sepDrawDownW4) - Number(data.sepRepaymentW1) + Number(data.sepRepaymentW2) + Number(data.sepRepaymentW3) + Number(data.sepRepaymentW4))',
          },
          {
            field: 'octEndingOutstanding',
            headerName: 'OCT',
            valueFormatter: formatGridNumber,
            cellStyle: { textAlign: 'right' },
            valueGetter:
              'Number(data.currentOs) + (Number(data.janDrawDownW1) + Number(data.janDrawDownW2) + Number(data.janDrawDownW3) + Number(data.janDrawDownW4) - Number(data.janRepaymentW1) + Number(data.janRepaymentW2) + Number(data.janRepaymentW3) + Number(data.janRepaymentW4)) + (Number(data.febDrawDownW1) + Number(data.febDrawDownW2) + Number(data.febDrawDownW3) + Number(data.febDrawDownW4) - Number(data.febRepaymentW1) + Number(data.febRepaymentW2) + Number(data.febRepaymentW3) + Number(data.febRepaymentW4)) + (Number(data.marDrawDownW1) + Number(data.marDrawDownW2) + Number(data.marDrawDownW3) + Number(data.marDrawDownW4) - Number(data.marRepaymentW1) + Number(data.marRepaymentW2) + Number(data.marRepaymentW3) + Number(data.marRepaymentW4)) + (Number(data.aprDrawDownW1) + Number(data.aprDrawDownW2) + Number(data.aprDrawDownW3) + Number(data.aprDrawDownW4) - Number(data.aprRepaymentW1) + Number(data.aprRepaymentW2) + Number(data.aprRepaymentW3) + Number(data.aprRepaymentW4)) + (Number(data.mayDrawDownW1) + Number(data.mayDrawDownW2) + Number(data.mayDrawDownW3) + Number(data.mayDrawDownW4) - Number(data.mayRepaymentW1) + Number(data.mayRepaymentW2) + Number(data.mayRepaymentW3) + Number(data.mayRepaymentW4)) + (Number(data.junDrawDownW1) + Number(data.junDrawDownW2) + Number(data.junDrawDownW3) + Number(data.junDrawDownW4) - Number(data.junRepaymentW1) + Number(data.junRepaymentW2) + Number(data.junRepaymentW3) + Number(data.junRepaymentW4)) + (Number(data.julDrawDownW1) + Number(data.julDrawDownW2) + Number(data.julDrawDownW3) + Number(data.julDrawDownW4) - Number(data.julRepaymentW1) + Number(data.julRepaymentW2) + Number(data.julRepaymentW3) + Number(data.julRepaymentW4)) + (Number(data.augDrawDownW1) + Number(data.augDrawDownW2) + Number(data.augDrawDownW3) + Number(data.augDrawDownW4) - Number(data.augRepaymentW1) + Number(data.augRepaymentW2) + Number(data.augRepaymentW3) + Number(data.augRepaymentW4)) + (Number(data.sepDrawDownW1) + Number(data.sepDrawDownW2) + Number(data.sepDrawDownW3) + Number(data.sepDrawDownW4) - Number(data.sepRepaymentW1) + Number(data.sepRepaymentW2) + Number(data.sepRepaymentW3) + Number(data.sepRepaymentW4)) + (Number(data.octDrawDownW1) + Number(data.octDrawDownW2) + Number(data.octDrawDownW3) + Number(data.octDrawDownW4) - Number(data.octRepaymentW1) + Number(data.octRepaymentW2) + Number(data.octRepaymentW3) + Number(data.octRepaymentW4))',
          },
          {
            field: 'novEndingOutstanding',
            headerName: 'NOV',
            valueFormatter: formatGridNumber,
            cellStyle: { textAlign: 'right' },
            valueGetter:
              'Number(data.currentOs) + (Number(data.janDrawDownW1) + Number(data.janDrawDownW2) + Number(data.janDrawDownW3) + Number(data.janDrawDownW4) - Number(data.janRepaymentW1) + Number(data.janRepaymentW2) + Number(data.janRepaymentW3) + Number(data.janRepaymentW4)) + (Number(data.febDrawDownW1) + Number(data.febDrawDownW2) + Number(data.febDrawDownW3) + Number(data.febDrawDownW4) - Number(data.febRepaymentW1) + Number(data.febRepaymentW2) + Number(data.febRepaymentW3) + Number(data.febRepaymentW4)) + (Number(data.marDrawDownW1) + Number(data.marDrawDownW2) + Number(data.marDrawDownW3) + Number(data.marDrawDownW4) - Number(data.marRepaymentW1) + Number(data.marRepaymentW2) + Number(data.marRepaymentW3) + Number(data.marRepaymentW4)) + (Number(data.aprDrawDownW1) + Number(data.aprDrawDownW2) + Number(data.aprDrawDownW3) + Number(data.aprDrawDownW4) - Number(data.aprRepaymentW1) + Number(data.aprRepaymentW2) + Number(data.aprRepaymentW3) + Number(data.aprRepaymentW4)) + (Number(data.mayDrawDownW1) + Number(data.mayDrawDownW2) + Number(data.mayDrawDownW3) + Number(data.mayDrawDownW4) - Number(data.mayRepaymentW1) + Number(data.mayRepaymentW2) + Number(data.mayRepaymentW3) + Number(data.mayRepaymentW4)) + (Number(data.junDrawDownW1) + Number(data.junDrawDownW2) + Number(data.junDrawDownW3) + Number(data.junDrawDownW4) - Number(data.junRepaymentW1) + Number(data.junRepaymentW2) + Number(data.junRepaymentW3) + Number(data.junRepaymentW4)) + (Number(data.julDrawDownW1) + Number(data.julDrawDownW2) + Number(data.julDrawDownW3) + Number(data.julDrawDownW4) - Number(data.julRepaymentW1) + Number(data.julRepaymentW2) + Number(data.julRepaymentW3) + Number(data.julRepaymentW4)) + (Number(data.augDrawDownW1) + Number(data.augDrawDownW2) + Number(data.augDrawDownW3) + Number(data.augDrawDownW4) - Number(data.augRepaymentW1) + Number(data.augRepaymentW2) + Number(data.augRepaymentW3) + Number(data.augRepaymentW4)) + (Number(data.sepDrawDownW1) + Number(data.sepDrawDownW2) + Number(data.sepDrawDownW3) + Number(data.sepDrawDownW4) - Number(data.sepRepaymentW1) + Number(data.sepRepaymentW2) + Number(data.sepRepaymentW3) + Number(data.sepRepaymentW4)) + (Number(data.octDrawDownW1) + Number(data.octDrawDownW2) + Number(data.octDrawDownW3) + Number(data.octDrawDownW4) - Number(data.octRepaymentW1) + Number(data.octRepaymentW2) + Number(data.octRepaymentW3) + Number(data.octRepaymentW4)) + (Number(data.novDrawDownW1) + Number(data.novDrawDownW2) + Number(data.novDrawDownW3) + Number(data.novDrawDownW4) - Number(data.novRepaymentW1) + Number(data.novRepaymentW2) + Number(data.novRepaymentW3) + Number(data.novRepaymentW4))',
          },
          {
            field: 'decEndingOutstanding',
            headerName: 'DEC',
            valueFormatter: formatGridNumber,
            cellStyle: { textAlign: 'right' },
            valueGetter:
              'Number(data.currentOs) + (Number(data.janDrawDownW1) + Number(data.janDrawDownW2) + Number(data.janDrawDownW3) + Number(data.janDrawDownW4) - Number(data.janRepaymentW1) + Number(data.janRepaymentW2) + Number(data.janRepaymentW3) + Number(data.janRepaymentW4)) + (Number(data.febDrawDownW1) + Number(data.febDrawDownW2) + Number(data.febDrawDownW3) + Number(data.febDrawDownW4) - Number(data.febRepaymentW1) + Number(data.febRepaymentW2) + Number(data.febRepaymentW3) + Number(data.febRepaymentW4)) + (Number(data.marDrawDownW1) + Number(data.marDrawDownW2) + Number(data.marDrawDownW3) + Number(data.marDrawDownW4) - Number(data.marRepaymentW1) + Number(data.marRepaymentW2) + Number(data.marRepaymentW3) + Number(data.marRepaymentW4)) + (Number(data.aprDrawDownW1) + Number(data.aprDrawDownW2) + Number(data.aprDrawDownW3) + Number(data.aprDrawDownW4) - Number(data.aprRepaymentW1) + Number(data.aprRepaymentW2) + Number(data.aprRepaymentW3) + Number(data.aprRepaymentW4)) + (Number(data.mayDrawDownW1) + Number(data.mayDrawDownW2) + Number(data.mayDrawDownW3) + Number(data.mayDrawDownW4) - Number(data.mayRepaymentW1) + Number(data.mayRepaymentW2) + Number(data.mayRepaymentW3) + Number(data.mayRepaymentW4)) + (Number(data.junDrawDownW1) + Number(data.junDrawDownW2) + Number(data.junDrawDownW3) + Number(data.junDrawDownW4) - Number(data.junRepaymentW1) + Number(data.junRepaymentW2) + Number(data.junRepaymentW3) + Number(data.junRepaymentW4)) + (Number(data.julDrawDownW1) + Number(data.julDrawDownW2) + Number(data.julDrawDownW3) + Number(data.julDrawDownW4) - Number(data.julRepaymentW1) + Number(data.julRepaymentW2) + Number(data.julRepaymentW3) + Number(data.julRepaymentW4)) + (Number(data.augDrawDownW1) + Number(data.augDrawDownW2) + Number(data.augDrawDownW3) + Number(data.augDrawDownW4) - Number(data.augRepaymentW1) + Number(data.augRepaymentW2) + Number(data.augRepaymentW3) + Number(data.augRepaymentW4)) + (Number(data.sepDrawDownW1) + Number(data.sepDrawDownW2) + Number(data.sepDrawDownW3) + Number(data.sepDrawDownW4) - Number(data.sepRepaymentW1) + Number(data.sepRepaymentW2) + Number(data.sepRepaymentW3) + Number(data.sepRepaymentW4)) + (Number(data.octDrawDownW1) + Number(data.octDrawDownW2) + Number(data.octDrawDownW3) + Number(data.octDrawDownW4) - Number(data.octRepaymentW1) + Number(data.octRepaymentW2) + Number(data.octRepaymentW3) + Number(data.octRepaymentW4)) + (Number(data.novDrawDownW1) + Number(data.novDrawDownW2) + Number(data.novDrawDownW3) + Number(data.novDrawDownW4) - Number(data.novRepaymentW1) + Number(data.novRepaymentW2) + Number(data.novRepaymentW3) + Number(data.novRepaymentW4)) + (Number(data.decDrawDownW1) + Number(data.decDrawDownW2) + Number(data.decDrawDownW3) + Number(data.decDrawDownW4) - Number(data.decRepaymentW1) + Number(data.decRepaymentW2) + Number(data.decRepaymentW3) + Number(data.decRepaymentW4))',
          },
        ],
      },
      {
        field: '',
        headerName: 'Actions',
        pinned: 'right',
        suppressSizeToFit: true,
        cellRendererFramework: (params: any) => {
          return <TooltipIconButton onClick={(e: any) => {}} icon={<Delete fontSize="small" />} title="Delete" />;
        },
      },
    ];
    return colDef;
  }, []);

  const { data: drawdownForecast } = useQuery('getDrawDownForecast', () => getDrawDownForecast(), {
    onSuccess: (data: any) => {
      setRowData(data);
    },
    onError: (err: any) => {
      console.log(err);
    },
  });

  // const _renderAddButton = () => {
  //   return (
  //     <Box sx={{ width: '100%' }}>
  //       <Button
  //         sx={{ p: 0, pl: 1, pr: 1, mt: 1, mb: 1, float: 'right' }}
  //         onClick={() => {}}
  //         variant="outlined"
  //         startIcon={<AddIcon fontSize="small" />}>
  //         Add new row
  //       </Button>
  //     </Box>
  //   );
  // };
  const handleSave = () => {
    console.log('rowdata', rowData);
    let drawdownForecast: any = saveDrawDownData(rowData);
  };
  return (
    <>
      <Box sx={{ p: 1, height: 'calc(100vh - 180px)' }}>
        <Box sx={{ height: '100%', mt: 1 }}>
          <DataTable
            columnDefs={drawdownColmDef}
            autoSize={true}
            rowData={rowData}
            sizeToFit={true}
            enableCellChangeFlash={true}
            suppressColumnVirtualisation={true}
            paginationPageSize={15}
            editType="single"
          />
        </Box>
        <ActionBar>
          <Button variant="contained" sx={{ mr: 2, p: 1 }} type="submit" onClick={handleSave}>
            Save Draft
          </Button>
        </ActionBar>
      </Box>
    </>
  );
};

export default DrawdownForecast;
